'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { TERMLY_ROOT_ID } from './constants';

declare global {
  interface Window {
    Termly?: {
      initialize: () => void;
    };
    displayPreferenceModal?: () => void;
  }
}

const LEGAL_PAGE_PATH = '/legal';
const TERMLY_ROOT_SELECTOR = `#${TERMLY_ROOT_ID}`;
const TERMLY_COOKIE_POLICY_SELECTOR =
  '[data-testid="cookie-policy-link"], a[href*="termly.io/policy-viewer"]';

function findTermlyPolicyLink(target: EventTarget | null) {
  if (!(target instanceof Element)) return null;
  if (!target.closest(TERMLY_ROOT_SELECTOR)) return null;

  return target.closest(TERMLY_COOKIE_POLICY_SELECTOR);
}

/**
 * Termly only lets us set a custom URL for the privacy policy; the cookie policy
 * link in the banner / preference center is hard-wired to open Termly's hosted
 * policy in a modal. Everything is covered on our /legal page, so intercept the
 * activation in the capture phase, before Termly's own handler runs, and send
 * the user there instead.
 */
function interceptTermlyPolicyLinks() {
  const redirect = (event: Event) => {
    if (!findTermlyPolicyLink(event.target)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    window.open(LEGAL_PAGE_PATH, '_blank', 'noopener');
  };

  const onClick = (event: MouseEvent) => redirect(event);
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    redirect(event);
  };

  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onKeyDown, true);

  return () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('keydown', onKeyDown, true);
  };
}

const SECONDARY_BUTTON_LABELS = ['Decline All', 'Discard Changes'];
const SECONDARY_BUTTON_ATTR = 'data-termly-secondary';

function markTermlySecondaryButtons() {
  const root = document.getElementById(TERMLY_ROOT_ID);
  if (!root) return;

  const mark = () => {
    root
      .querySelectorAll(`button:not([${SECONDARY_BUTTON_ATTR}])`)
      .forEach((button) => {
        if (SECONDARY_BUTTON_LABELS.includes(button.textContent?.trim() ?? '')) {
          button.setAttribute(SECONDARY_BUTTON_ATTR, '');
        }
      });
  };

  const observer = new MutationObserver(mark);
  observer.observe(root, { childList: true, subtree: true });
  mark();

  return () => observer.disconnect();
}

export function TermlyCMP() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => interceptTermlyPolicyLinks(), []);
  useEffect(() => markTermlySecondaryButtons(), []);

  useEffect(() => {
    if (!window.Termly) return;

    try {
      window.Termly.initialize();
    } catch {
      // Termly not ready yet; the embed script initializes itself on load.
    }
  }, [pathname, searchParams]);

  return null;
}

export function openTermlyPreferences() {
  if (typeof window === 'undefined') return;

  window.displayPreferenceModal?.();
}
